import React, { useCallback, useEffect, useMemo, useState } from "react";
import { bool, func, shape, string } from "prop-types";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { compose } from "redux";
import yup from "yup";
import { EcheqsMassivesManagementConfirmation } from "@ui-kit/components/index.es";

import Head from "pages/_components/Head";
import Notification from "pages/_components/Notification";
import { resizableRoute } from "pages/_components/Resizable";
import {
    actions as echeqMassManagementActions,
    selectors as echeqMassManagementSelectors,
} from "reducers/echeqMassManagement";
import { actions as notificationActions } from "reducers/notification";
import { selectors as sessionSelectors } from "reducers/session";
import { actions as tokenActions, selectors as tokenSelectors } from "reducers/token";
import formatLegacyWebDateTimeString from "util/formatLegacyWebDateTimeString";
import * as i18n from "util/i18n";
import isTokenActive from "util/token";
import { useEmbeddedV3ShellLayoutSync } from "util/v3ShellLayoutSync";

import { ECHEQS_MASS_MANAGEMENT_NEW_PATH } from "./echeqMassManageConstants";
import { createI18nLabelResolver } from "./echeqMassManagePageUtils";
import {
    ECHEQ_MASS_MANAGE_DETAIL_ROWS_PER_PAGE,
    mapSummaryToTicketDisplaySummary,
    mapValidDetailRows,
} from "./echeqMassManageUtils";

const FORM_ID = "echeqs.massives.manage.confirm";

function EcheqsMassManageConfirm({
    dispatch,
    isDesktop,
    embeddedInV3Shell,
    summary,
    fetchingSend,
    userFullName,
    tokenNumber,
    fetchingToken,
}) {
    const [detailPageNumber, setDetailPageNumber] = useState(1);

    useEffect(() => {
        if (!summary) {
            dispatch(push(ECHEQS_MASS_MANAGEMENT_NEW_PATH));
        }
    }, [dispatch, summary]);

    useEffect(() => {
        if (!isDesktop && isTokenActive()) {
            dispatch(tokenActions.softTokenGenerationRequest());
        }
    }, [dispatch, isDesktop]);

    const handleGoBack = useCallback(() => {
        dispatch(push(ECHEQS_MASS_MANAGEMENT_NEW_PATH));
    }, [dispatch]);

    useEmbeddedV3ShellLayoutSync(dispatch, {
        embeddedInV3Shell,
        isDesktop,
        titleKey: `${FORM_ID}.${summary?.action || "admit"}.title`,
        goBackPath: ECHEQS_MASS_MANAGEMENT_NEW_PATH,
        scopes: ["echeqs"],
    });

    const getLabels = useMemo(() => createI18nLabelResolver(), []);

    const detailItems = useMemo(() => mapValidDetailRows(summary?.details, summary?.echeqsQuantity), [
        summary?.details,
        summary?.echeqsQuantity,
    ]);

    const detailTotalPages = useMemo(
        () => Math.max(1, Math.ceil(detailItems.length / ECHEQ_MASS_MANAGE_DETAIL_ROWS_PER_PAGE)),
        [detailItems.length],
    );

    // Desktop: una página (Pagination). Mobile infinite: acumula hasta la página actual.
    const overlayDetailItems = useMemo(() => {
        if (isDesktop) {
            const start = (detailPageNumber - 1) * ECHEQ_MASS_MANAGE_DETAIL_ROWS_PER_PAGE;
            return detailItems.slice(start, start + ECHEQ_MASS_MANAGE_DETAIL_ROWS_PER_PAGE);
        }
        return detailItems.slice(0, detailPageNumber * ECHEQ_MASS_MANAGE_DETAIL_ROWS_PER_PAGE);
    }, [detailItems, detailPageNumber, isDesktop]);

    useEffect(() => {
        setDetailPageNumber(1);
    }, [detailItems.length]);

    const handleDetailPageChange = useCallback((page) => {
        if (page < 1) {
            return;
        }
        setDetailPageNumber(page);
    }, []);

    const handleViewDetailUnavailable = useCallback(() => {
        dispatch(notificationActions.showNotification(i18n.get(`${FORM_ID}.viewDetail.empty`), "warning", ["echeqs"]));
    }, [dispatch]);

    const confirmationSummary = useMemo(() => {
        if (!summary) {
            return null;
        }

        return mapSummaryToTicketDisplaySummary(summary);
    }, [summary]);

    const getToken = useCallback(async () => {
        if (!isDesktop && isTokenActive() && tokenNumber) {
            return { token: tokenNumber };
        }
        return { token: null };
    }, [isDesktop, tokenNumber]);

    const otpSchema = useMemo(
        () =>
            yup.object({
                otp:
                    !isDesktop && isTokenActive() && tokenNumber
                        ? yup.string().nullable()
                        : yup.string().required(i18n.get(`${FORM_ID}.otp.required`)),
            }),
        [isDesktop, tokenNumber],
    );

    const handleConfirm = useCallback(
        ({ otp }, { setSubmitting }) => {
            if (!summary) {
                setSubmitting(false);
                return Promise.resolve();
            }

            const finalOtp = !isDesktop && isTokenActive() && tokenNumber ? tokenNumber : otp;

            return new Promise((resolve) => {
                dispatch(
                    echeqMassManagementActions.sendRequest(summary, finalOtp, () => {
                        setSubmitting(false);
                        resolve();
                    }),
                );
            });
        },
        [dispatch, summary, isDesktop, tokenNumber],
    );

    const confirmationProps = useMemo(
        () => ({
            confirmationText: i18n.get("form.credential.hint"),
            handleConfirm,
            loading: fetchingSend,
            isInactive: fetchingSend || (!isDesktop && isTokenActive() && fetchingToken),
            getToken,
            validationConfirm: otpSchema,
            initialValue: { otp: "" },
        }),
        [fetchingSend, fetchingToken, getToken, handleConfirm, isDesktop, otpSchema],
    );

    if (!summary || !confirmationSummary) {
        return null;
    }

    return (
        <>
            {!embeddedInV3Shell && <Notification scopeToShow="echeqs" />}
            {!embeddedInV3Shell && !isDesktop && (
                <div className="admin-detail-head px-0 mb-0">
                    {isDesktop ? <Head onBack={handleGoBack} /> : undefined}
                    <Head
                        title={`${FORM_ID}.${summary.action}.title`}
                        isFetchingExport={false}
                        onBack={!isDesktop ? handleGoBack : undefined}
                        headerClassName={
                            !isDesktop ? "blue-main-header-mobile servive-payment-title-mobile" : undefined
                        }
                    />
                </div>
            )}
            <EcheqsMassivesManagementConfirmation
                goBack={handleGoBack}
                title={i18n.get(`${FORM_ID}.${summary.action}.title`)}
                subtitle={i18n.get(`${FORM_ID}.subtitle`)}
                getLabels={getLabels}
                summary={confirmationSummary}
                userFullName={userFullName}
                creatorFullName={userFullName}
                formatSignatureDate={formatLegacyWebDateTimeString}
                viewDetailLabel={i18n.get(`${FORM_ID}.viewDetail`)}
                detailItems={overlayDetailItems}
                detailPaginationProps={{
                    totalItems: detailItems.length,
                    ...(detailTotalPages > 1
                        ? {
                              totalPages: detailTotalPages,
                              pageNumber: detailPageNumber,
                              action: handleDetailPageChange,
                          }
                        : {}),
                }}
                onViewDetail={detailItems.length === 0 ? handleViewDetailUnavailable : undefined}
                confirmation={confirmationProps}
            />
        </>
    );
}

EcheqsMassManageConfirm.propTypes = {
    dispatch: func.isRequired,
    isDesktop: bool.isRequired,
    embeddedInV3Shell: bool,
    summary: shape({}),
    fetchingSend: bool,
    userFullName: string,
    tokenNumber: string,
    fetchingToken: bool,
};

EcheqsMassManageConfirm.defaultProps = {
    embeddedInV3Shell: false,
    summary: null,
    fetchingSend: false,
    userFullName: "",
    tokenNumber: null,
    fetchingToken: false,
};

const mapStateToProps = (state) => ({
    summary: echeqMassManagementSelectors.getSummary(state),
    fetchingSend: echeqMassManagementSelectors.getFetchingSend(state),
    userFullName: sessionSelectors.getUserFullName(state),
    tokenNumber: tokenSelectors.getTokenNumber(state),
    fetchingToken: tokenSelectors.getFetchingToken(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(EcheqsMassManageConfirm);
